import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonTypes, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { SuccessCard } from '@eika/card';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { IconTypes } from '@eika/icon';

import { ErrorCardWrapper } from '../../../../components/shared/ErrorCardWrapper';
import { Fieldset } from '../../../../components/shared/Fieldset';
import { NavigationButtons } from '../../../../components/shared/NavigationButtons';
import type { EFakturaCreditor, EFakturaGivenPermission } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { eFakturaTrackingIDs } from '../../../../utils/gtm/trackIDs/eFaktura';
import { AmountSelectedType } from '../../form/state';
import useFormContext from '../../form/useFormContext';
import routes from '../../routes';
import { AmountSelectors } from '../AmountSelectors';
import { CreditorPanels } from '../CreditorPanels';

type Props = {
    user?: EFakturaGivenPermission;
    onSubmit: () => void;
    amountSelected: AmountSelectedType;
    setAmountSelected: (value: AmountSelectedType) => void;
    selected: EFakturaCreditor[];
    setSelected: (value: EFakturaCreditor[]) => void;
    hasChanged: boolean;
    error?: string;
    setErrorMessage: (value: string | undefined) => void;
    locked?: boolean;
};

const UpdateGivenPermission: React.FC<Props> = ({
    user,
    onSubmit,
    amountSelected,
    setAmountSelected,
    selected,
    setSelected,
    hasChanged,
    error,
    setErrorMessage,
    locked = false,
}) => {
    const navigate = useNavigate();

    const translated = useTranslation();
    const { deleteGiven } = useFormContext();
    const { signingOperationPending } = useSigningContext();

    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        if (deleteGiven.isSuccess) {
            const timer = setTimeout(() => {
                navigate(routes.home);
            }, 1500);
            return () => clearTimeout(timer);
        }
        return () => null;
    }, [deleteGiven.isSuccess, navigate]);

    if (deleteGiven.error) {
        return (
            <ErrorCardWrapper exception={deleteGiven.error}>
                <ButtonWithSpinner
                    showSpinner={deleteGiven.isLoading}
                    buttonType={ButtonTypes.STANDARD_POSITIVE}
                    onClick={() => (user?.id ? deleteGiven.mutate(user.id) : navigate(routes.home))}
                >
                    {translated.tryAgain}
                </ButtonWithSpinner>
            </ErrorCardWrapper>
        );
    }

    if (deleteGiven.isSuccess) {
        return (
            <SuccessCard title={translated.eFakturaSharing.receiverDeleted}>
                <Button buttonType={ButtonTypes.STANDARD_POSITIVE} onClick={() => navigate(routes.home)}>
                    {translated.ok}
                </Button>
            </SuccessCard>
        );
    }

    return (
        <>
            <form onSubmit={onSubmit} className="efaktura-sharing__update-shared-user-form">
                <Fieldset className="efaktura-sharing__update-shared-user" legend={user?.disponentName ?? ''}>
                    <AmountSelectors
                        locked={locked}
                        value={amountSelected}
                        onChange={setAmountSelected}
                        resetSelectedArray={() => setSelected([])}
                        validationError={amountSelected === 'some' ? undefined : error}
                        setValidationError={() => setErrorMessage(undefined)}
                    />

                    {amountSelected === 'some' && (
                        <CreditorPanels
                            locked={locked}
                            onChange={setSelected}
                            selected={selected}
                            userToBeUpdated={user?.id}
                        />
                    )}
                </Fieldset>
                {user?.id && (
                    <>
                        {showConfirmation ? (
                            <ConfirmationDialog
                                title={format(translated.eFakturaSharing.confirmDelete, user.disponentName)}
                                id={`user-delete-${user.id.value}`}
                                description={translated.eFakturaSharing.confirmDeleteDescription}
                                footer={
                                    <div>
                                        <LinkButton
                                            onClick={() => setShowConfirmation(false)}
                                            data-track-id={eFakturaTrackingIDs.deleteSharedEFaktura.id}
                                            data-track-linkname="navigate__cancel"
                                        >
                                            {translated.cancel}
                                        </LinkButton>
                                        <ButtonWithSpinner
                                            buttonType={ButtonTypes.STANDARD_POSITIVE}
                                            showSpinner={deleteGiven.isLoading}
                                            onClick={() => deleteGiven.mutate(user.id)}
                                            showSuccess={deleteGiven.isSuccess}
                                            data-track-id={eFakturaTrackingIDs.deleteSharedEFaktura.id}
                                            data-track-linkname="submit"
                                            data-testid="confrim-shared-efaktura-deletion"
                                        >
                                            {translated.delete}
                                        </ButtonWithSpinner>
                                    </div>
                                }
                                show={showConfirmation}
                                blueBackground
                                onExitFocus={() => setShowConfirmation(false)}
                            />
                        ) : (
                            <LinkButton
                                className="efaktura-sharing__delete-shared-user"
                                icon={IconTypes.SLETT_24}
                                onClick={() => setShowConfirmation(true)}
                                data-track-id={eFakturaTrackingIDs.deleteSharedEFaktura.id}
                                data-track-linkname="navigate__confirm"
                                data-testid="delete-shared-efaktura-button"
                            >
                                {translated.eFakturaSharing.deleteSharing}
                            </LinkButton>
                        )}
                    </>
                )}
                <NavigationButtons
                    className="efaktura-sharing__navigation-buttons"
                    centerOnSmallerWidth
                    hidePrevButton
                    paymentButton
                    showNextButton={hasChanged}
                    paymentButtonText={translated.save}
                    cancelUrl={routes.cancel}
                    paymentOperationPending={signingOperationPending}
                    dataTrackId={eFakturaTrackingIDs.updateSharedEFaktura.id}
                />
            </form>
        </>
    );
};

export default UpdateGivenPermission;
